import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Card,
  FormHelperText
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import palette from 'src/theme/palette';
import { toast } from 'react-toastify';
import { addFaqs, getFaqsCategory } from 'src/ApiServices';
import { handleRestrictedInputChange } from 'src/utlis/faq-validation';
import useMediaQuery from '@mui/material/useMediaQuery';

// Rich Text Editor imports
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AddEditFaqModal = ({ open, onClose, onSubmit, editingFaq }) => {
  const initialFormState = {
    faq_category_id: '',
    title: '',
    description: '',
    answer: '',
    meta_title: '',
    meta_description: '',
    bg_video: null
  };

  const [formData, setFormData] = useState(initialFormState);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState('');
  const [fileType, setFileType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [answerError, setAnswerError] = useState('');
  const isMobile = useMediaQuery('(max-width:600px)');
  // Rich Text Editor modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ align: [] }],
      ['blockquote', 'code-block'],
      ['link', 'image', 'video'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'color',
    'background',
    'script',
    'list',
    'bullet',
    'indent',
    'align',
    'blockquote',
    'code-block',
    'link',
    'image',
    'video'
  ];

  // Fetch categories when modal opens
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const response = await getFaqsCategory();
        setCategories(response?.data || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error('Failed to load categories');
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    if (open) {
      fetchCategories();
    }
  }, [open]);

  // useEffect(() => {
  //   if (open) {
  //     if (editingFaq) {
  //       // Load existing FAQ data for editing
  //       setFormData({
  //         faq_category_id: editingFaq.faq_category_id || '',
  //         title: editingFaq.title || '',
  //         description: editingFaq.description || '',
  //         answer: editingFaq.answer || '',
  //         meta_title: editingFaq.meta_title || '',
  //         meta_description: editingFaq.meta_description || '',
  //         bg_video: editingFaq.bg_video
  //       });

  //       // Set file preview for existing files
  //       if (editingFaq.bg_video) {
  //         const existingFileType = getFileTypeFromUrl(editingFaq.bg_video);
  //         setFileType(existingFileType);
  //         setFilePreview(editingFaq.bg_video);
  //       } else {
  //         setFilePreview('');
  //         setFileType('');
  //       }
  //     } else {
  //     }
  //   }
  // }, [open, editingFaq]);

  useEffect(() => {
    if (!open) return;

    if (editingFaq) {
      setFormData({
        faq_category_id: editingFaq.faq_category_id || '',
        title: editingFaq.title || '',
        description: editingFaq.description || '',
        answer: editingFaq.answer || '',
        meta_title: editingFaq.meta_title || '',
        meta_description: editingFaq.meta_description || '',
        bg_video: editingFaq.bg_video || null
      });

      if (editingFaq.bg_video) {
        const type = getFileTypeFromUrl(editingFaq.bg_video);
        setFileType(type);
        setFilePreview(editingFaq.bg_video);
      }
    } else {
      setFormData(initialFormState);
      setSelectedFile(null);
      setFilePreview('');
      setFileType('');
      setAnswerError('');
    }
  }, [open, editingFaq]);

  const getFileTypeFromUrl = (url) => {
    if (!url) return '';
    const extension = url.split('.').pop()?.toLowerCase();
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];

    return imageExtensions.includes(extension)
      ? 'image'
      : videoExtensions.includes(extension)
      ? 'video'
      : 'file';
  };

  const getFileTypeFromFile = (file) => {
    if (!file) return '';
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    return 'file';
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAnswerChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      answer: value
    }));

    // Validate answer
    const error = validateAnswerField(value);
    setAnswerError(error);
  };

  const validateAnswerField = (value) => {
    const strippedValue = value?.replace(/<[^>]*>/g, '').trim();
    return strippedValue ? '' : 'Detailed answer is required';
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check if file is image or video
      const type = getFileTypeFromFile(file);

      if (type !== 'image' && type !== 'video') {
        toast.error('Please select a valid image or video file');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        toast.error('File size should be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setFileType(type);

      if (type === 'image') {
        const previewUrl = URL.createObjectURL(file);
        setFilePreview(previewUrl);
      } else {
        // For videos, create object URL for preview
        const videoUrl = URL.createObjectURL(file);
        setFilePreview(videoUrl);
      }

      setFormData((prev) => ({ ...prev, bg_video: file }));
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreview('');
    setFileType('');
    setFormData((prev) => ({ ...prev, bg_video: null }));
    const fileInput = document.getElementById('faq-media-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!formData.faq_category_id || !formData.title || !formData.description || !formData.answer) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate answer field
    const answerValidationError = validateAnswerField(formData.answer);
    setAnswerError(answerValidationError);

    if (answerValidationError) {
      toast.error('Please fill the detailed answer field');
      return;
    }

    try {
      setSubmitting(true);

      const submitData = new FormData();
      submitData.append('faq_category_id', formData.faq_category_id);
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('answer', formData.answer);
      submitData.append('meta_title', formData.meta_title);
      submitData.append('meta_description', formData.meta_description);

      if (editingFaq) {
        submitData.append('id', editingFaq.id);
      }

      if (selectedFile) {
        submitData.append('bg_video', selectedFile);
      }

      await addFaqs(submitData);
      await onSubmit();
    } catch (error) {
      console.error('Error submitting FAQ:', error);
      toast.error(error.response?.data?.message || 'Failed to save FAQ');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          maxHeight: isMobile ? '100vh' : '90vh',
          width: isMobile ? '100vw' : 'auto',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      {/* Header - Same Theme */}
      <DialogTitle
        sx={{
          background: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontFamily: 'poppinsSemiBold',
            color: palette.primary.contrastText
          }}
        >
          {editingFaq ? 'Edit FAQ' : 'Add New FAQ'}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Category Dropdown */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select
                  name="faq_category_id"
                  value={formData.faq_category_id}
                  onChange={handleInputChange}
                  disabled={submitting || categoriesLoading}
                  label="Category *"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: palette.primary.inputborder
                      },
                      '&:hover fieldset': {
                        borderColor: palette.primary.main
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: palette.primary.main
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: palette.primary.title
                    }
                  }}
                >
                  {categoriesLoading ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      Loading categories...
                    </MenuItem>
                  ) : (
                    categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* Question (Title) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Question *"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                disabled={submitting}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: palette.primary.inputborder
                    },
                    '&:hover fieldset': {
                      borderColor: palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary.main
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: palette.primary.title
                  }
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Short Answer (Max 150 characters) *"
                name="description"
                value={formData.description}
                onChange={(e) => handleRestrictedInputChange(e, setFormData, 'description')}
                disabled={submitting}
                error={formData.description.length > 150}
                helperText={
                  formData.description.length > 150
                    ? `Maximum 150 characters allowed (${formData.description.length}/150)`
                    : `${formData.description.length}/150`
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: palette.primary.inputborder
                    },
                    '&:hover fieldset': {
                      borderColor: palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary.main
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: palette.primary.title
                  }
                }}
              />
            </Grid>

            {/* Answer - HTML Editor */}
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.primary.titledark,
                  mb: 1
                }}
              >
                Detailed Answer *
              </Typography>

              <Box
                sx={{
                  border: answerError
                    ? `1px solid ${palette.error.main}`
                    : `1px solid ${palette.primary.inputborder}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                  '& .ql-toolbar': {
                    border: 'none',
                    borderBottom: `1px solid ${palette.primary.inputborder}`,
                    backgroundColor: palette.background.neutral,
                    '& .ql-picker-label': {
                      fontFamily: 'poppinsRegular'
                    },
                    '& .ql-picker-options': {
                      fontFamily: 'poppinsRegular'
                    }
                  },
                  '& .ql-container': {
                    border: 'none',
                    fontFamily: 'poppinsRegular',
                    fontSize: '14px',
                    minHeight: '300px'
                  },
                  '& .ql-editor': {
                    minHeight: '300px',
                    fontFamily: 'poppinsRegular',
                    '& h1, & h2, & h3, & h4, & h5, & h6': {
                      fontFamily: 'poppinsSemiBold',
                      marginTop: '8px',
                      marginBottom: '8px'
                    },
                    '& a': {
                      color: palette.primary.main,
                      textDecoration: 'underline',
                      '&:hover': {
                        color: palette.primary.dark
                      }
                    },
                    '& img': {
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'all 0.3s ease',
                      maxWidth: '100%',
                      height: 'auto',
                      margin: '8px 0',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        boxShadow: `0 4px 12px ${palette.primary.main}40`
                      }
                    },
                    '&.ql-blank::before': {
                      color: palette.text.disabled,
                      fontStyle: 'normal',
                      fontFamily: 'poppinsRegular'
                    }
                  },
                  '&:hover': {
                    borderColor: palette.primary.main
                  },
                  '&:focus-within': {
                    borderColor: palette.primary.main,
                    borderWidth: 2
                  }
                }}
              >
                <ReactQuill
                  value={formData.answer}
                  onChange={handleAnswerChange}
                  modules={modules}
                  formats={formats}
                  placeholder="Enter detailed answer..."
                  theme="snow"
                />
              </Box>

              {answerError && (
                <FormHelperText
                  error
                  sx={{
                    ml: 0,
                    mt: 1
                  }}
                >
                  {answerError}
                </FormHelperText>
              )}
            </Grid>

            {/* Meta Title and Meta Description in same row */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Meta Title"
                name="meta_title"
                value={formData.meta_title}
                onChange={handleInputChange}
                disabled={submitting}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: palette.primary.inputborder
                    },
                    '&:hover fieldset': {
                      borderColor: palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary.main
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: palette.primary.title
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                rows={1}
                label="Meta Description"
                name="meta_description"
                value={formData.meta_description}
                onChange={handleInputChange}
                disabled={submitting}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: palette.primary.inputborder
                    },
                    '&:hover fieldset': {
                      borderColor: palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: palette.primary.main
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: palette.primary.title
                  }
                }}
              />
            </Grid>

            {/* Image/Video Upload */}
            <Grid item xs={12}>
              <Card
                sx={{
                  border: `2px dashed ${palette.primary.inputborder}`,
                  borderRadius: 1,
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: palette.background.paper,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: palette.primary.main,
                    backgroundColor: palette.primary.highlightBLue
                  }
                }}
              >
                <input
                  accept="image/*,video/*"
                  style={{ display: 'none' }}
                  id="faq-media-upload"
                  type="file"
                  onChange={handleFileChange}
                  disabled={submitting}
                />
                <label htmlFor="faq-media-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    disabled={submitting}
                    sx={{
                      borderColor: palette.primary.inputborder,
                      color: palette.primary.title,
                      '&:hover': {
                        borderColor: palette.primary.main,
                        backgroundColor: palette.primary.hover
                      }
                    }}
                  >
                    Upload Background Image/Video
                  </Button>
                </label>

                {(filePreview || (editingFaq && editingFaq.bg_video)) && (
                  <Box sx={{ mt: 2 }}>
                    {fileType === 'image' ||
                    getFileTypeFromUrl(editingFaq?.bg_video) === 'image' ? (
                      // Image Preview
                      <Box sx={{ textAlign: 'center' }}>
                        <img
                          src={filePreview || editingFaq.bg_video}
                          alt="Preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: '8px',
                            marginBottom: '10px',
                            border: `1px solid ${palette.grey[300]}`
                          }}
                        />
                      </Box>
                    ) : fileType === 'video' ||
                      getFileTypeFromUrl(editingFaq?.bg_video) === 'video' ? (
                      // Video Preview
                      <Box sx={{ textAlign: 'center' }}>
                        <video
                          controls
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: '8px',
                            marginBottom: '10px',
                            border: `1px solid ${palette.grey[300]}`
                          }}
                        >
                          <source src={filePreview || editingFaq.bg_video} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </Box>
                    ) : null}

                    <Box>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleRemoveFile}
                        disabled={submitting}
                        sx={{
                          borderColor: palette.error.main,
                          color: palette.error.main,
                          '&:hover': {
                            borderColor: palette.error.dark,
                            backgroundColor: palette.error.lighter
                          }
                        }}
                      >
                        Remove File
                      </Button>
                    </Box>
                  </Box>
                )}

                <Typography variant="body2" sx={{ color: palette.primary.title, mt: 1 }}>
                  Click to upload a background image or video (JPEG, PNG, MP4, etc.) - Max 10MB
                </Typography>

                {selectedFile && (
                  <Typography variant="body2" sx={{ color: palette.success.main, mt: 0.5 }}>
                    Selected: {selectedFile.name} ({fileType})
                  </Typography>
                )}

                {editingFaq?.bg_video && !selectedFile && (
                  <Typography variant="body2" sx={{ color: palette.info.main, mt: 0.5 }}>
                    Current: {editingFaq.bg_video.split('/').pop()} (
                    {getFileTypeFromUrl(editingFaq.bg_video)})
                  </Typography>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {/* Footer Buttons */}
      <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          // onClick={handleClear}
          onClick={handleClose}
          disabled={submitting}
          sx={{
            borderColor: palette.primary.inputborder,
            color: palette.primary.title,
            '&:hover': {
              borderColor: palette.primary.main,
              backgroundColor: palette.primary.hover
            }
          }}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            !formData.faq_category_id ||
            !formData.title ||
            !formData.description ||
            !formData.answer ||
            submitting ||
            answerError
          }
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            },
            '&:disabled': {
              backgroundColor: palette.primary.disabled,
              color: palette.text.disabled
            }
          }}
        >
          {submitting ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              <span>{editingFaq ? 'Updating...' : 'Saving...'}</span>
            </Box>
          ) : editingFaq ? (
            'Update FAQ'
          ) : (
            'Save FAQ'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditFaqModal;
